// Wave 14 — Unified analytics dashboard page (route: /dashboard).
//
// Replaces /sessions (mocked list) + /positions/analytics. Single fetch
// to GET /sessions/me/dashboard returns:
//   - thin sessions list
//   - power-vs-CdA scatter samples + hasPowerData flag
//   - CdA-vs-duration curve
//   - the existing positionsAnalytics rollup
// Hero is the two new charts side-by-side. Below: recent sessions,
// per-position cards, time distribution, and Pro-gated cross-position
// matrix + best-by-context cards.

import { refAeroSpeedLabel, useUnitSystem } from './services/useUnitSystem';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageLayout from './components/PageLayout';
import PowerCdaScatter, { PowerCdaSample } from './components/PowerCdaScatter';
import CdaDurationCurve, { CdaDurationPoint } from './components/CdaDurationCurve';
import UsageDashboardCard from './components/UsageDashboardCard';
import PaywallModal from './components/PaywallModal';
import { colors } from './theme';
import { positionColor } from './services/positionColors';
import { useSubscription } from './services/useSubscription';

type SessionThin = {
  id: string;
  source: string;
  startedAt: string;
  endedAt: string | null;
  durationSec: number | null;
  bestCda: number | null;
  bestPosition: string | null;
  avgPowerW: number | null;
  maxPowerW: number | null;
};

type PositionStats = {
  class: string;
  bestCda: number;
  meanCda: number;
  worstCda: number;
  totalHoldSec: number;
  sessionCount: number;
  firstSeen: string;
  lastSeen: string;
  bestSessionId: string;
  recentBests: Array<{ sessionId: string; startedAt: string; bestCda: number }>;
};

type DashboardPayload = {
  sessions: SessionThin[];
  powerCdaSamples: PowerCdaSample[];
  hasPowerData: boolean;
  cdaDurationCurve: CdaDurationPoint[];
  positions: {
    positions: PositionStats[];
    crossPositionWattsAt40: Record<string, Record<string, number>>;
    totalSessions: number;
    lifetimeBest: { class: string | null; cda: number | null };
  };
};

function formatDuration(sec: number | null): string {
  if (sec == null) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatTotalTime(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  const s = Math.round(sec % 60);
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

function Sparkline({
  data,
  color,
}: {
  data: Array<{ startedAt: string; bestCda: number }>;
  color: string;
}) {
  const W = 220;
  const H = 60;
  if (!data || data.length < 2) {
    return (
      <Box sx={{ height: H, display: 'flex', alignItems: 'center' }}>
        <Typography sx={{ color: colors.textFaint, fontSize: '0.75rem', fontStyle: 'italic' }}>
          Not enough sessions yet
        </Typography>
      </Box>
    );
  }
  const values = data.map((d) => d.bestCda);
  const minV = Math.min(...values);
  const maxV = Math.max(...values);
  const pad = Math.max((maxV - minV) * 0.2, 0.005);
  const yMin = minV - pad;
  const yMax = maxV + pad;
  const points = data
    .map((d, i) => {
      const x = (i / (data.length - 1)) * W;
      const y = ((yMax - d.bestCda) / (yMax - yMin)) * H;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H }}>
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={W}
        cy={((yMax - data[data.length - 1].bestCda) / (yMax - yMin)) * H}
        r={3}
        fill={color}
      />
    </svg>
  );
}

function SectionCard(props: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 3,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Stack spacing={0.5} sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: colors.textMuted,
          }}
        >
          {props.title}
        </Typography>
        {props.subtitle && (
          <Typography sx={{ fontSize: '0.8125rem', color: colors.textFaint }}>
            {props.subtitle}
          </Typography>
        )}
      </Stack>
      {props.children}
    </Box>
  );
}

export default function Analytics() {
  const units = useUnitSystem();
  const { t } = useTranslation('analytics');
  const navigate = useNavigate();
  const { hasFullAccess } = useSubscription();

  const [data, setData] = useState<DashboardPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [paywallOpen, setPaywallOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem(
      process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token',
    );
    if (!token) {
      navigate('/login');
      return;
    }
    const base = process.env.REACT_APP_API || '';
    let cancelled = false;
    fetch(`${base}/sessions/me/dashboard`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (r.status === 402 && !cancelled) setPaywallOpen(true);
        return r.ok ? r.json() : null;
      })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const totalHoldSec = useMemo(() => {
    if (!data) return 0;
    return data.positions.positions.reduce((sum, p) => sum + p.totalHoldSec, 0);
  }, [data]);

  const bestByContext = useMemo(() => {
    if (!data || data.positions.positions.length === 0) return null;
    const ps = data.positions.positions;
    return {
      lowestCda: [...ps].sort((a, b) => a.bestCda - b.bestCda)[0],
      mostSustained: [...ps].sort((a, b) => b.totalHoldSec - a.totalHoldSec)[0],
      mostConsistent: [...ps].sort(
        (a, b) => a.worstCda - a.bestCda - (b.worstCda - b.bestCda),
      )[0],
      mostRecent: [...ps].sort((a, b) => (a.lastSeen < b.lastSeen ? 1 : -1))[0],
    };
  }, [data]);

  if (loading) {
    return (
      <PageLayout maxWidth="lg">
        <Typography sx={{ color: colors.textMuted }}>{t('loading')}</Typography>
      </PageLayout>
    );
  }

  if (!data || (data.positions.positions.length === 0 && data.sessions.length === 0)) {
    return (
      <PageLayout maxWidth="md">
        <Stack spacing={2} sx={{ mt: 6, alignItems: 'flex-start' }}>
          <Typography variant="overline" sx={{ color: colors.brand }}>
            {t('overline')}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            {t('empty.title')}
          </Typography>
          <Typography sx={{ color: colors.textMuted, maxWidth: 540 }}>
            {t('empty.body')}
          </Typography>
          <Button
            component={RouterLink}
            to="/session"
            sx={{
              mt: 1,
              backgroundColor: colors.brand,
              color: colors.bg,
              fontWeight: 700,
              letterSpacing: '0.05em',
              px: 3,
              '&:hover': { backgroundColor: colors.brand },
            }}
          >
            {t('empty.cta')}
          </Button>
        </Stack>
      </PageLayout>
    );
  }

  const lifetimeBest = data.positions.lifetimeBest;
  const recentSessions = data.sessions.slice(0, 6);
  const positionsList = hasFullAccess
    ? data.positions.positions
    : data.positions.positions.slice(0, 2);

  return (
    <PageLayout maxWidth="lg">
      <Stack spacing={3}>
        <Box>
          <Typography variant="overline" sx={{ color: colors.brand }}>
            {t('overline')}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5 }}>
            {t('title')}
          </Typography>
          <Typography sx={{ color: colors.textMuted, mt: 0.5 }}>{t('subtitle')}</Typography>
        </Box>
        <UsageDashboardCard />

        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Box sx={{ p: 2.5, borderRadius: 3, backgroundColor: colors.surface, border: `1px solid ${colors.borderFocus}` }}>
              <Typography sx={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textMuted }}>
                {t('header.lifetimeBest')}
              </Typography>
              <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '2rem', fontWeight: 800, color: colors.brand, mt: 0.5 }}>
                {lifetimeBest.cda != null ? lifetimeBest.cda.toFixed(3) : '—'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={3}>
            <Box sx={{ p: 2.5, borderRadius: 3, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <Typography sx={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textMuted }}>
                {t('header.lifetimeBestPosition')}
              </Typography>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, mt: 0.5, color: lifetimeBest.class ? positionColor(lifetimeBest.class) : colors.textMuted }}>
                {lifetimeBest.class ?? '—'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={3}>
            <Box sx={{ p: 2.5, borderRadius: 3, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <Typography sx={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textMuted }}>
                {t('header.totalTime')}
              </Typography>
              <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '1.5rem', fontWeight: 700, color: colors.text, mt: 0.5 }}>
                {formatTotalTime(totalHoldSec)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={3}>
            <Box sx={{ p: 2.5, borderRadius: 3, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <Typography sx={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textMuted }}>
                {t('header.totalSessions')}
              </Typography>
              <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '1.5rem', fontWeight: 700, color: colors.text, mt: 0.5 }}>
                {data.positions.totalSessions}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <SectionCard title={t('hero.powerCda.title')} subtitle={t('hero.powerCda.subtitle')}>
              <PowerCdaScatter samples={data.hasPowerData ? data.powerCdaSamples : []} />
            </SectionCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <SectionCard title={t('hero.duration.title')} subtitle={t('hero.duration.subtitle')}>
              <CdaDurationCurve curve={data.cdaDurationCurve} />
            </SectionCard>
          </Grid>
        </Grid>

        {recentSessions.length > 0 && (
          <SectionCard title={t('recent.title')} subtitle={t('recent.subtitle')}>
            <Grid container spacing={2}>
              {recentSessions.map((session) => (
                <Grid item xs={12} md={6} key={session.id}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      backgroundColor: colors.surfaceElevated,
                      border: `1px solid ${
                        session.bestCda ? colors.borderFocus : colors.border
                      }`,
                      transition: 'all 220ms',
                      '&:hover': { borderColor: colors.borderFocus, transform: 'translateY(-2px)' },
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: colors.textFaint }}>
                        {formatDate(session.startedAt)}
                      </Typography>
                      {session.bestPosition && (
                        <Chip
                          label={session.bestPosition}
                          size="small"
                          sx={{
                            backgroundColor: 'transparent',
                            border: `1px solid ${positionColor(session.bestPosition)}`,
                            color: positionColor(session.bestPosition),
                            fontSize: '0.625rem',
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                          }}
                        />
                      )}
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={3}
                      sx={{ mb: 1.5 }}
                      divider={<Box sx={{ width: '1px', backgroundColor: colors.border }} />}
                    >
                      <Box>
                        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: colors.textMuted }}>
                          {t('recent.col.cda')}
                        </Typography>
                        <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '1.5rem', fontWeight: 700, color: colors.brand, lineHeight: 1.1 }}>
                          {session.bestCda != null ? session.bestCda.toFixed(3) : '—'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: colors.textMuted }}>
                          {t('recent.col.avgPower')}
                        </Typography>
                        <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '1.5rem', fontWeight: 700, color: colors.text, lineHeight: 1.1 }}>
                          {session.avgPowerW != null ? `${session.avgPowerW}W` : '—'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: colors.textMuted }}>
                          {t('recent.col.duration')}
                        </Typography>
                        <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '1.5rem', fontWeight: 700, color: colors.text, lineHeight: 1.1 }}>
                          {formatDuration(session.durationSec)}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTimeIcon sx={{ fontSize: 14, color: colors.textMuted }} />
                        <Typography variant="caption" sx={{ color: colors.textMuted }}>
                          {session.source}
                        </Typography>
                      </Box>
                      <Button
                        component={RouterLink}
                        to={`/sessions/${session.id}`}
                        endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                        sx={{
                          color: colors.brand,
                          fontSize: '0.75rem',
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          fontWeight: 700,
                          px: 0,
                          '&:hover': { backgroundColor: 'transparent' },
                        }}
                      >
                        {t('recent.viewDetail')}
                      </Button>
                    </Stack>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </SectionCard>
        )}

        {data.positions.positions.length > 0 && (
          <SectionCard title={t('positions.title')} subtitle={t('positions.subtitle')}>
            <Grid container spacing={2}>
              {positionsList.map((p) => (
                <Grid item xs={12} md={6} key={p.class}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      border: `1px solid ${colors.border}`,
                      backgroundColor: colors.surfaceElevated,
                      height: '100%',
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                      <Chip
                        label={p.class}
                        size="small"
                        sx={{
                          backgroundColor: 'transparent',
                          border: `1px solid ${positionColor(p.class)}`,
                          color: positionColor(p.class),
                          fontSize: '0.6875rem',
                          letterSpacing: '0.05em',
                          textTransform: 'uppercase',
                        }}
                      />
                      <Typography sx={{ fontSize: '0.6875rem', color: colors.textFaint }}>
                        {t('positions.firstSeen', { date: new Date(p.firstSeen).toLocaleDateString() })}
                      </Typography>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textMuted }}>
                          {t('positions.bestCda')}
                        </Typography>
                        <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '1.5rem', fontWeight: 700, color: colors.brand, lineHeight: 1.1 }}>
                          {p.bestCda.toFixed(3)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textMuted }}>
                          {t('positions.meanCda')}
                        </Typography>
                        <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '1.5rem', fontWeight: 700, color: colors.text, lineHeight: 1.1 }}>
                          {p.meanCda.toFixed(3)}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Box sx={{ mt: 2 }}>
                      <Sparkline data={p.recentBests} color={positionColor(p.class)} />
                    </Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: colors.textMuted }}>
                        {t('positions.totalTime', {
                          time: formatTotalTime(p.totalHoldSec),
                          sessions: p.sessionCount,
                        })}
                      </Typography>
                      <Button
                        component={RouterLink}
                        to={`/sessions/${p.bestSessionId}`}
                        size="small"
                        sx={{
                          color: colors.brand,
                          fontSize: '0.6875rem',
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          fontWeight: 700,
                          px: 0,
                          '&:hover': { backgroundColor: 'transparent' },
                        }}
                      >
                        {t('positions.viewSession')}
                      </Button>
                    </Stack>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </SectionCard>
        )}

        {totalHoldSec > 0 && (
          <SectionCard title={t('timeDistribution.title')} subtitle={t('timeDistribution.subtitle')}>
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                height: 24,
                borderRadius: 1,
                overflow: 'hidden',
                backgroundColor: colors.surfaceElevated,
              }}
            >
              {data.positions.positions.map((p) => {
                const pct = (p.totalHoldSec / totalHoldSec) * 100;
                if (pct <= 0) return null;
                return (
                  <Box
                    key={p.class}
                    sx={{
                      height: '100%',
                      width: `${pct}%`,
                      backgroundColor: positionColor(p.class),
                      opacity: 0.85,
                    }}
                    title={`${p.class}: ${pct.toFixed(1)}%`}
                  />
                );
              })}
            </Box>
            <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 2 }}>
              {data.positions.positions.map((p) => {
                const pct = (p.totalHoldSec / totalHoldSec) * 100;
                return (
                  <Stack direction="row" spacing={1} alignItems="center" key={p.class}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: positionColor(p.class) }} />
                    <Typography sx={{ color: colors.textMuted, fontSize: '0.8125rem' }}>
                      {p.class} · {pct.toFixed(1)}%
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </SectionCard>
        )}

        {hasFullAccess ? (
          <>
            {data.positions.positions.length > 1 && (
              <SectionCard title={t('matrix.title')} subtitle={t('matrix.subtitle', { refSpeed: refAeroSpeedLabel(units) })}>
                <Box sx={{ overflowX: 'auto' }}>
                  <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', minWidth: 480 }}>
                    <thead>
                      <tr>
                        <Box component="th" sx={{ p: 1.25, textAlign: 'left', color: colors.textMuted, fontSize: '0.6875rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                          {t('matrix.from')} \ {t('matrix.to')}
                        </Box>
                        {data.positions.positions.map((p) => (
                          <Box component="th" key={p.class} sx={{ p: 1.25, color: positionColor(p.class), fontSize: '0.6875rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                            {p.class}
                          </Box>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.positions.positions.map((row) => (
                        <tr key={row.class}>
                          <Box component="th" sx={{ p: 1.25, textAlign: 'left', color: positionColor(row.class), fontSize: '0.75rem', textTransform: 'uppercase' }}>
                            {row.class}
                          </Box>
                          {data.positions.positions.map((col) => {
                            const watts = data.positions.crossPositionWattsAt40?.[row.class]?.[col.class] ?? 0;
                            const same = row.class === col.class;
                            const intensity = Math.min(1, Math.abs(watts) / 50);
                            const cellColor = same
                              ? 'transparent'
                              : watts >= 0
                              ? `rgba(106, 255, 69, ${0.08 + intensity * 0.4})`
                              : `rgba(255, 71, 87, ${0.08 + intensity * 0.4})`;
                            return (
                              <Box
                                component="td"
                                key={col.class}
                                sx={{
                                  p: 1.25,
                                  textAlign: 'center',
                                  backgroundColor: cellColor,
                                  fontFamily: '"JetBrains Mono", monospace',
                                  fontSize: '0.8125rem',
                                  color: same ? colors.textFaint : colors.text,
                                }}
                              >
                                {same
                                  ? '—'
                                  : `${watts > 0 ? '+' : ''}${watts.toFixed(1)} W`}
                              </Box>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </Box>
                </Box>
              </SectionCard>
            )}

            {bestByContext && (
              <SectionCard title={t('bestByContext.title')}>
                <Grid container spacing={2}>
                  {[
                    {
                      key: 'lowestCda',
                      p: bestByContext.lowestCda,
                      value: bestByContext.lowestCda.bestCda.toFixed(3),
                    },
                    {
                      key: 'mostSustained',
                      p: bestByContext.mostSustained,
                      value: formatTotalTime(bestByContext.mostSustained.totalHoldSec),
                    },
                    {
                      key: 'mostConsistent',
                      p: bestByContext.mostConsistent,
                      value: `Δ ${(bestByContext.mostConsistent.worstCda - bestByContext.mostConsistent.bestCda).toFixed(3)}`,
                    },
                    {
                      key: 'mostRecent',
                      p: bestByContext.mostRecent,
                      value: new Date(bestByContext.mostRecent.lastSeen).toLocaleDateString(),
                    },
                  ].map((b) => (
                    <Grid item xs={12} sm={6} md={3} key={b.key}>
                      <Box sx={{ p: 2, borderRadius: 2, border: `1px solid ${colors.border}`, backgroundColor: colors.surfaceElevated }}>
                        <Typography sx={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textMuted }}>
                          {t(`bestByContext.${b.key}`)}
                        </Typography>
                        <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: positionColor(b.p.class), mt: 0.5 }}>
                          {b.p.class}
                        </Typography>
                        <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', color: colors.text, fontSize: '0.875rem' }}>
                          {b.value}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </SectionCard>
            )}
          </>
        ) : (
          <SectionCard title={t('upgrade.title')} subtitle={t('upgrade.body')}>
            <Button
              component={RouterLink}
              to="/subscription"
              variant="contained"
              sx={{
                mt: 1,
                backgroundColor: colors.brand,
                color: colors.bg,
                '&:hover': { backgroundColor: colors.brand },
                fontWeight: 700,
                letterSpacing: '0.05em',
              }}
            >
              {t('upgrade.cta')}
            </Button>
          </SectionCard>
        )}
      </Stack>
      <PaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        reason="locked_feature"
        featureName="Analytics dashboard"
      />
    </PageLayout>
  );
}
